import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { WarningIcon, CheckCircleIcon, InfoOutlineIcon } from '@chakra-ui/icons';

const denialData = [
  {
    carc: 'CARC 97',
    title: 'Payment Included in Another Service',
    context: 'Duplicate Same-Type Encounter \u2014 or a Wrong Denial',
    description: 'PA pays multiple same-day encounters of DIFFERENT types (medical + dental + BH + vision), but two encounters of the SAME type on one date deny with CARC 97 unless a distinct later-day illness/injury is documented. MCOs also sometimes bundle different-type encounters incorrectly \u2014 those denials are wrong in PA.',
    revedySolution: 'Revedy reads both clinical notes: if the second encounter is a distinct same-type visit, it drafts the Box 80 (Remarks) justification pre-submission; if the payer bundled different encounter types, it drafts an appeal citing the DHS FQHC/RHC same-day policy instead.',
    colorScheme: 'red'
  },
  {
    carc: 'CARC 16',
    title: 'Lacks Information / Billing Error',
    context: 'T1015 Line Order & Modifier Errors',
    description: 'PA FQHC claims must carry T1015 on the first claim line with detail services as zero-pay tracking lines, plus the correct type modifiers (e.g., U3 vision encounters, U9 teledentistry with POS 02). Getting the line order or modifiers wrong triggers CARC 16.',
    revedySolution: 'Revedy validates line order and encounter-type modifiers against the OMAP MA Bulletins and the FQHC PROMISe Provider Handbook, auto-correcting the claim before it reaches the clearinghouse.',
    colorScheme: 'orange'
  },
  {
    carc: 'CARC 22',
    title: 'Covered by Another Payer (COB)',
    context: 'Carve-Out Misroutes & TPL',
    description: 'Two PA-specific flavors: behavioral health services billed to the physical-health MCO instead of the county BH-MCO (CBH in Philadelphia), and claims sent to the MCO when the state TPL file shows an active commercial primary \u2014 Medicaid is the payer of last resort.',
    revedySolution: 'Revedy checks the county-to-BH-MCO crosswalk and the PROMISe TPL segment before submission, re-routing BH claims to the correct BH-MCO and sequencing commercial primaries ahead of the MCO.',
    colorScheme: 'blue'
  },
  {
    carc: 'CARC 50',
    title: 'Medical Necessity Not Met',
    context: 'Unsupported Procedure Codes',
    description: 'The billed ICD-10 diagnosis does not support the procedure under the payer\u2019s medical policy \u2014 for PA MA, the MCO\u2019s clinical policy bulletins and OMAP guidance govern.',
    revedySolution: 'The Revedy LLM reads the full clinical note, identifies a documented but unbilled supporting diagnosis (e.g., diabetic polyneuropathy), and proposes adding it to the claim to satisfy medical necessity.',
    colorScheme: 'purple'
  }
];

const FqPaDemoDenialIntelligence = () => {
  return (
    <Container maxW="container.xl" py={8}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="lg" color="brand.700" mb={2}>Pennsylvania FQHC Denial Intelligence</Heading>
          <Text color="gray.600">
            Demonstrating Revedy's deep understanding of common Claim Adjustment Reason Codes (CARC) as they play out for Pennsylvania FQHCs under HealthChoices and PROMISe, and how our intelligence layer automatically prevents or resolves them.
          </Text>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          {denialData.map((denial, idx) => (
            <Card key={idx} variant="outline" borderColor={`${denial.colorScheme}.200`} bg="white">
              <CardHeader bg={`${denial.colorScheme}.50`} pb={3}>
                <HStack justify="space-between" align="start">
                  <VStack align="start" spacing={1}>
                    <Badge colorScheme={denial.colorScheme} fontSize="md" px={2} py={1} borderRadius="md">
                      {denial.carc}
                    </Badge>
                    <Heading size="sm" color={`${denial.colorScheme}.700`}>{denial.title}</Heading>
                  </VStack>
                  <Badge variant="outline" colorScheme={denial.colorScheme}>
                    {denial.context}
                  </Badge>
                </HStack>
              </CardHeader>
              <CardBody>
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <HStack mb={2}>
                      <Icon as={WarningIcon} color="gray.500" />
                      <Text fontWeight="bold" fontSize="sm" color="gray.700">The Pennsylvania Challenge</Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                      {denial.description}
                    </Text>
                  </Box>
                  <Divider />
                  <Box bg="green.50" p={3} borderRadius="md" borderWidth="1px" borderColor="green.200">
                    <HStack mb={2} align="start">
                      <Icon as={CheckCircleIcon} color="green.500" mt={1} />
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="green.800">The Revedy Solution</Text>
                        <Text fontSize="sm" color="green.700" mt={1}>
                          {denial.revedySolution}
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      </VStack>
    </Container>
  );
};

export default FqPaDemoDenialIntelligence;
